import React, { useState, useMemo } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { paisesSelector } from '../../appStore/commonSlice';
import { nameMap } from '../../utils';
import CVItem, { DocLink, MultiLineData } from './CVItem';
import { formatDate, transformDateFormat } from './cv.helpers';
import { cvTiposSelector } from './cvSlice';
import { FormAcademicos } from './itemForms/FormAcademicos';
import { FormCapacitaciones } from './itemForms/FormCapacitaciones';
import { FormIdiomas } from './itemForms/FormIdiomas';
import { FormInformatico } from './itemForms/FormInformatico';
import { FormInstitucionales } from './itemForms/FormInstitucionales';
import { FormLaborales } from './itemForms/FormLaborales';
import { FormPublicaciones } from './itemForms/FormPublicaciones';

//
// ─── GENERICO ───────────────────────────────────────────────────────────────────
//
/**
 * Representa el contenido de una sección. engloba el formulario de item y el listado de item,
 * extrae factores comunes (botón de agregar, estado del modal)
 * @param {} props
 */
export const SeccionContext = React.createContext();

const SeccionContent = ({ form: CustomForm, children, type, botonMas }) => {
  const [show, setShow] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [formTitle, setFormTitle] = useState('Agregar ');

  const state = useMemo(() => {
    return {
      type,
      show,
      handleClose: () => {
        setShow(false);
        setInitialValues({});
      },
      handleOpen: () => setShow(true),
      initialValues,
      setInitialValues,
      formTitle,
      setFormTitle,
    };
  }, [type, show, initialValues, formTitle]);

  const vacio = <i className="text-muted">No hay items cargados en esta sección </i>;
  return (
    <SeccionContext.Provider value={state}>
      <CustomForm />
      {children.length > 0 ? children : vacio}
      {botonMas && (
        <Row>
          <Col className="d-flex justify-content-end">
            <Button
              variant="success"
              title="Agregar"
              onClick={() => {
                setFormTitle('Agregar');
                setInitialValues({});
                setShow(true);
              }}>
              Agregar
              {/* <i className="fa fa-fw fa-plus"></i> */}
            </Button>
          </Col>
        </Row>
      )}
    </SeccionContext.Provider>
  );
};

//
// ─── ACADEMICOS ──────────────────────────────────────────────────────────
//
export const ItemsAcademicos = ({ items, acciones = true, validarItems, idItems, botonMas }) => {
  const tipos = useSelector(cvTiposSelector).tiposAcademicos;
  const paises = useSelector(paisesSelector);
  const order = [
    'tipo',
    'establecimiento',
    'matriculaHabilitante',
    'f_ingreso',
    'f_egreso',
    'pais',
    'homologado',
    'nroActoAdministrativo',
    'documentoPath',
    'documentDate',
  ];

  const nameMapAcademico = {
    ...nameMap,
    establecimiento: 'Establecimiento que emite la titulación',
    tipo: 'Nivel de acreditación',
    // documentDate: 'Fecha de carga de la documentación',
  };
  return (
    <SeccionContent form={FormAcademicos} type="antecedentesAcademicos" botonMas={botonMas}>
      {items.map((el, idx) => {
        const item = {
          ...el,
          tipo: tipos[el.idTipoAntecedenteAcademico].nombre,
          pais: paises[el.idPais]?.nombre,
          homologado: paises[el.idPais].nombre !== 'Argentina' && (el.homologado ? 'SI' : 'NO'),
          f_ingreso: el.f_ingreso
            ? transformDateFormat(el.f_ingreso)
            : 'No se selecciono fecha de ingreso.',
          f_egreso: el.f_egreso ? transformDateFormat(el.f_egreso) : 'Actualidad',
          documentoPath: el.documentoPath && (
            <DocLink
              docPath={el.documentoPath}
              docDate={el.documentDate ? formatDate(el.documentDate) : formatDate(new Date())}
            />
          ),
        };

        return (
          <CVItem
            key={idx}
            titleKey="acreditacionObtenida"
            item={item}
            order={order}
            acciones={acciones}
            validarItems={validarItems}
            realItem={{ ...el, actualidad: !el.f_egreso }}
            nameMap={nameMapAcademico}
            idItems={idItems}
          />
        );
      })}
    </SeccionContent>
  );
};

//
// ─── CAPACITACIONES ─────────────────────────────────────────────────────
//
export const ItemsCapacitaciones = ({
  items,
  acciones = true,
  validarItems,
  idItems,
  botonMas,
}) => {
  const tipos = useSelector(cvTiposSelector).tiposCursos;
  const order = [
    'duracion',
    'institucion',
    'pais',
    'tipo',
    'f_inicio',
    'f_fin',
    'documentoPath',
    'documentDate',
  ];
  const paises = useSelector(paisesSelector);

  const capacitacionesNameMap = {
    ...nameMap,
    institucion: 'Establecimiento o Institución',
  };

  return (
    <SeccionContent form={FormCapacitaciones} type="capacitacion" botonMas={botonMas}>
      {items.map((el, idx) => {
        const item = {
          ...el,
          tipo: tipos[el.idTipoCurso].nombre,
          f_inicio: el.f_inicio
            ? transformDateFormat(el.f_inicio)
            : 'No se selecciono fecha de inicio.',
          f_fin: el.f_fin ? transformDateFormat(el.f_fin) : 'Actualidad',
          pais: paises[el.idPais]?.nombre,
          duracion: `${el.duracion} Hs.`,
          documentoPath: el.documentoPath && (
            <DocLink
              docPath={el.documentoPath}
              docDate={el.documentDate ? formatDate(el.documentDate) : formatDate(new Date())}
            />
          ),
        };

        return (
          <CVItem
            key={idx}
            titleKey="nombre"
            item={item}
            acciones={acciones}
            validarItems={validarItems}
            order={order}
            realItem={el}
            nameMap={capacitacionesNameMap}
            idItems={idItems}
          />
        );
      })}
    </SeccionContent>
  );
};

//
// ─── LABORALES ────────────────────────────────────────────────────────────
//
export const ItemsLaborales = ({ items, acciones = true, validarItems, idItems, botonMas }) => {
  const tipos = useSelector(cvTiposSelector).tiposLaborales;
  const order = [
    'sector',
    'lugarTrabajo',
    'pais',
    'descripcionTarea',
    'personalACargo',
    'f_desde',
    'f_hasta',
    'documentoPath',
    'documentoPath2',
    'documentDate',
    'documentDate2',
  ];
  const paises = useSelector(paisesSelector);

  const nameMapLaborales = {
    ...nameMap,
    sector: 'Sector',
    documentoPath: 'Certificación de servicios',
  };

  return (
    <SeccionContent form={FormLaborales} type="antecedentesLaborales" botonMas={botonMas}>
      {items.map((el, idx) => {
        const item = {
          ...el,
          descripcionTarea: <MultiLineData texto={el.descripcionTarea} />,
          sector: tipos[el.idTipoAntecedenteLaboral].descripcion,
          f_desde: el.f_desde ? transformDateFormat(el.f_desde) : 'No se selecciono fecha desde.',
          f_hasta: el.f_hasta ? transformDateFormat(el.f_hasta) : 'Actualidad',
          pais: paises[el.idPais]?.nombre,
          documentoPath: el.documentoPath && (
            <DocLink
              docPath={el.documentoPath}
              docDate={el.documentDate ? formatDate(el.documentDate) : formatDate(new Date())}
            />
          ),
          documentoPath2: el.documentoPath2 && (
            <DocLink
              docPath={el.documentoPath2}
              docDate={el.documentDate ? formatDate(el.documentDate) : formatDate(new Date())}
            />
          ),
        };

        return (
          <CVItem
            key={idx}
            titleKey="cargo"
            item={item}
            acciones={acciones}
            validarItems={validarItems}
            realItem={{
              ...el,
              actualidad: !el.f_hasta,
              tienePersonalACargo: el.personalACargo > 0,
            }}
            nameMap={nameMapLaborales}
            order={order}
            altDoc={true}
            idItems={idItems}
          />
        );
      })}
    </SeccionContent>
  );
};

//
// ─── INSTITUCIONALES ────────────────────────────────────────────────────────────
//
export const ItemsInstitucionales = ({
  items,
  acciones = true,
  validarItems,
  idItems,
  botonMas,
}) => {
  const data = useSelector(cvTiposSelector);
  const niveles = data.nivelEducativo;

  const order = [
    'nivelEducativo',
    'otroPuestoDocente',
    'asignatura',
    'institucion',
    'descripcionTarea',
    'cargaHoraria',
    'frecuenciaDeLaMateria',
    'f_desde',
    'f_hasta',
    'documentoPath',
    'documentDate',
  ];
  const nameMapInstitucionales = {
    ...nameMap,
    tipo: 'Tipo',
  };

  return (
    <SeccionContent form={FormInstitucionales} type="institucionales" botonMas={botonMas}>
      {items.map((el, idx) => {
        const item = {
          ...el,
          descripcionTarea: <MultiLineData texto={el.descripcionTarea} />,
          nivelEducativo: niveles[el.idNivelEducativoCv]?.descripcion,
          cargaHoraria: `${el.cargaHorariaSemanalDeLaMateria} Hs.`,
          f_desde: el.f_desde ? transformDateFormat(el.f_desde) : 'No se selecciono fecha desde.',
          f_hasta: el.f_hasta ? transformDateFormat(el.f_hasta) : 'Actualidad',
          documentoPath: el.documentoPath && (
            <DocLink
              docPath={el.documentoPath}
              docDate={el.documentDate ? formatDate(el.documentDate) : formatDate(new Date())}
            />
          ),
        };

        //Busco el cargo por id dentro del arreglo de cargos del nivelEducativo.
        const cargo = (niveles[el.idNivelEducativoCv]?.cargo || []).filter(
          (elem) => el.idCargoCv === elem.idCargoCv,
        )[0];

        return (
          <CVItem
            key={idx}
            titleKey="idCargoCv"
            titleValue={cargo?.descripcion}
            item={item}
            acciones={acciones}
            validarItems={validarItems}
            realItem={{ ...el, actualidad: !el.f_hasta }}
            nameMap={nameMapInstitucionales}
            order={order}
            idItems={idItems}
          />
        );
      })}
    </SeccionContent>
  );
};

//
// ─── PUBLICACIONES ──────────────────────────────────────────────────────────────
//
export const ItemsPublicaciones = ({ items, acciones = true, validarItems, idItems, botonMas }) => {
  const tipos = useSelector(cvTiposSelector).tiposPublicaciones;
  const paises = useSelector(paisesSelector);
  const order = [
    'tipo',
    'pais',
    'cantPaginas',
    'isbn',
    'f_presentacion',
    'institucionOrganizadora',
    'linkPublicacion',
    'documentoPath',
    'documentDate',
    'sintesis',
  ];

  const publiNameMap = {
    ...nameMap,
    tipo: 'Tipo',
    sintesis: 'Síntesis de la temática de la publicación',
    cantPaginas: 'Cantidad de páginas',
    isbn: 'ISBN',
  };
  return (
    <SeccionContent form={FormPublicaciones} type="publicaciones" botonMas={botonMas}>
      {items.map((el, idx) => {
        const item = {
          ...el,
          sintesis: <MultiLineData texto={el.sintesis} />,
          f_presentacion: transformDateFormat(el.f_presentacion),
          tipo: tipos[el.idTipoPublicaciones].nombre,
          pais: paises[el.idPais]?.nombre,
          linkPublicacion: el.linkPublicacion && (
            <a href={el.linkPublicacion} target="_blank" rel="noopener noreferrer">
              {el.linkPublicacion}
            </a>
          ),
          documentoPath: el.documentoPath && (
            <DocLink
              docPath={el.documentoPath}
              docDate={el.documentDate ? formatDate(el.documentDate) : formatDate(new Date())}
            />
          ),
        };

        return (
          <CVItem
            key={idx}
            titleKey="titulo"
            item={item}
            acciones={acciones}
            validarItems={validarItems}
            realItem={el}
            nameMap={publiNameMap}
            order={order}
            idItems={idItems}
          />
        );
      })}
    </SeccionContent>
  );
};

//
// ─── IDIOMAS ────────────────────────────────────────────────────────────────────
//
export const ItemsIdiomas = ({ items, acciones = true, validarItems, idItems, botonMas }) => {
  const niveles = useSelector(cvTiposSelector).nivelesIdiomas;
  const paises = useSelector(paisesSelector);

  const idiomaNameMap = {
    ...nameMap,
    nivel: 'Nivel',
    institucion: 'Institución o establecimiento',
    f_certificacion: 'Fecha de certificación',
  };

  const order = [
    'nombre',
    'nivel',
    'pais',
    'f_certificacion',
    'institucion',
    'documentoPath',
    'documentDate',
  ];
  return (
    <SeccionContent form={FormIdiomas} type="idiomas" botonMas={botonMas}>
      {items.map((el, idx) => {
        const item = {
          ...el,
          nivel: <span>{niveles[el.idNivelIdiomaExtranjero].nombre}</span>,
          pais: paises[el.idPais]?.nombre,
          f_certificacion: el.f_certificacion && transformDateFormat(el.f_certificacion),
          documentoPath: el.documentoPath && (
            <DocLink
              docPath={el.documentoPath}
              docDate={el.documentDate ? formatDate(el.documentDate) : formatDate(new Date())}
            />
          ),
        };
        return (
          <CVItem
            key={idx}
            titleKey="nombre"
            item={item}
            acciones={acciones}
            validarItems={validarItems}
            realItem={el}
            nameMap={idiomaNameMap}
            order={order}
            idItems={idItems}
          />
        );
      })}
    </SeccionContent>
  );
};

//
// ─── CONOCIMIENTOS INFORMATICOS ─────────────────────────────────────────────────
//
export const ItemsInformaticos = ({ items, acciones = true, validarItems, idItems, botonMas }) => {
  const niveles = useSelector(cvTiposSelector).nivelInformatico;
  const paises = useSelector(paisesSelector);

  const informaticoNameMap = {
    ...nameMap,
    nivel: 'Nivel',
    institucion: 'Institución o establecimiento',
    f_certificacion: 'Fecha de certificación',
  };
  const order = ['descripcion', 'nivel', 'pais', 'f_certificacion', 'institucion', 'documentoPath'];
  return (
    <SeccionContent form={FormInformatico} type="informaticos" botonMas={botonMas}>
      {items.map((el, idx) => {
        const item = {
          ...el,
          nivel: <span>{niveles[el.idNivelConocimientoInformatico].nombre}</span>,
          pais: paises[el.idPais]?.nombre,
          f_certificacion: el.f_certificacion && transformDateFormat(el.f_certificacion),
          documentoPath: el.documentoPath && (
            <DocLink
              docPath={el.documentoPath}
              docDate={el.documentDate ? formatDate(el.documentDate) : formatDate(new Date())}
            />
          ),
        };
        return (
          <CVItem
            key={idx}
            titleKey="descripcion"
            item={item}
            acciones={acciones}
            validarItems={validarItems}
            realItem={el}
            nameMap={informaticoNameMap}
            order={order}
            idItems={idItems}
          />
        );
      })}
    </SeccionContent>
  );
};
